import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useSurveyCardsStore } from "@stores/survey/surveyCards";
import { useContactsStore } from "@stores/survey/mailing/contacts";
import { useCurrentUserStore } from "@stores/currentUser";
import { getChatBaseUrl } from "@utils/constants/chatUrls";

// Module-level state: These refs are intentionally shared across all components
// using this composable to maintain consistent simulation state
const currentCountry = ref("");
const currentLocaleSimu = ref("");
const currentTestContact = ref("");
const chatRef = ref();
const currentCard = ref("");
const pParam = ref("");
const extraParams = ref("");
const lastSurveyToken = ref("");

const resetSimulationState = () => {
  currentCountry.value = "";
  currentLocaleSimu.value = "";
  currentTestContact.value = "";
  currentCard.value = "";
  pParam.value = "";
  extraParams.value = "";
};

export const useSurveySimulation = (survey, routeParams) => {
  if (lastSurveyToken.value && lastSurveyToken.value !== survey.token) {
    resetSimulationState();
  }
  lastSurveyToken.value = survey.token;
  const surveyLocalesStore = useSurveyLocalesStore();
  const surveyCardsStore = useSurveyCardsStore();
  const contactsStore = useContactsStore();
  const currentUserStore = useCurrentUserStore();

  const { targetCountries, isMultiCountry, countryOptions } = storeToRefs(useSurveyTargetsStore());
  const { localesTokens, isMultiLocale, currentLocale } = storeToRefs(surveyLocalesStore);
  const { contacts, getContactOptions } = storeToRefs(contactsStore);
  const { isAdmin } = storeToRefs(currentUserStore);

  const targetCountryOptions = computed(() =>
    countryOptions.value.filter(({ value }) => targetCountries.value.includes(value)),
  );

  const determineCurrentCountry = () => (currentCountry.value = targetCountries.value?.[0]);
  const determineCurrentLocale = () =>
    (currentLocaleSimu.value = currentLocale.value?.token ?? localesTokens.value?.[0]);
  const determineCurrentTestContact = () => (currentTestContact.value = contacts.value?.[0]?.token);

  const updateCurrentCountry = (country) => {
    reloadChat();
    currentCountry.value = country;
  };

  const chatBaseURL = computed(() => getChatBaseUrl());

  const shareLink = computed(() => {
    const s = survey.chat_token;
    const lg = currentLocale.value?.language;
    const locale = currentLocaleSimu.value;
    const cc = currentCountry.value;
    const contactToken = currentTestContact.value;
    let baseURL = `${chatBaseURL.value}/?s=${s}&lg=${lg}-${locale}&cc=${cc}`;

    if (currentTestContact.value !== "" && currentTestContact.value != null) {
      const contact = contacts.value.find(({ token }) => token === contactToken);
      baseURL += `&encid=${contact.token}&listid=${contact.list_token}`;
    }

    const queryParams = Object.entries(routeParams).filter(([name]) => !["lg", "card"].includes(name));
    const queryString = queryParams.length ? `&${queryParams.map(([key, value]) => `${key}=${value}`).join("&")}` : "";

    return `${baseURL}${queryString}`;
  });

  const handleChatMessage = ({ data }) => {
    const { currentCardToken } = data;
    if (currentCardToken == null) return;
    currentCard.value = currentCardToken;
  };

  const resetProgression = () => {
    currentCard.value = "";
  };

  const buildTestUrl = (extra = "") => {
    let url = `${shareLink.value}&test=1${extra}`;
    if (pParam.value) url = `${url}&p=${pParam.value}`;
    if (extraParams.value) url = `${url}&${extraParams.value}`;
    return url;
  };

  const reloadChat = () => {
    resetProgression();
    chatRef.value.src = buildTestUrl();
  };

  const goToCard = ({ token }) => {
    resetProgression();
    surveyCardsStore.clearSearchQuery();
    currentCard.value = token;
    const cardParam = surveyCardsStore.findCardByToken({ token })?.name ?? "";
    chatRef.value.src = `${shareLink.value}&test=1&keep=1&card=${cardParam}`;
  };

  const hasMailingAccess = computed(() => survey.has_mailing_access === true || isAdmin.value);
  const isBrevoTest = computed(() => hasMailingAccess.value && contacts.value.length > 0);
  const testContactOptions = computed(() => getContactOptions.value);
  const updateTestContact = (contact) => {
    reloadChat();
    currentTestContact.value = contact;
  };

  return {
    currentCountry,
    currentLocaleSimu,
    currentTestContact,
    currentCard,
    pParam,
    extraParams,
    determineCurrentCountry,
    determineCurrentLocale,
    determineCurrentTestContact,
    isMultiCountry,
    isMultiLocale,
    targetCountryOptions,
    testContactOptions,
    updateCurrentCountry,
    updateTestContact,
    shareLink,
    handleChatMessage,
    reloadChat,
    buildTestUrl,
    chatRef,
    goToCard,
    isBrevoTest,
    hasMailingAccess,
  };
};
